import React, { useState, useEffect } from 'react';
import { PlusIcon, CalendarIcon, InformationCircleIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';

const AddStepButton = ({ phaseId, onStepAdded, label = "Add Step" }) => {
  const [showModal, setShowModal] = useState(false);
  const [stepName, setStepName] = useState('');
  const [stepDescription, setStepDescription] = useState('');
  const [stepDueDate, setStepDueDate] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nextOrderIndex, setNextOrderIndex] = useState(null);
  const [loadingOrderIndex, setLoadingOrderIndex] = useState(false);

  // Fetch the next order index when the modal opens
  useEffect(() => {
    const fetchNextOrderIndex = async () => {
      if (!showModal || !phaseId) return;
      
      try {
        setLoadingOrderIndex(true);
        const response = await api.get(`/steps?phase_id=${phaseId}`);
        if (response.data && response.data.steps) {
          const steps = response.data.steps;
          if (steps.length === 0) {
            setNextOrderIndex(0);
          } else {
            // Find the highest order_index and add 1
            const highestIndex = Math.max(...steps.map(step => step.order_index));
            setNextOrderIndex(highestIndex + 1);
          }
        }
      } catch (error) {
        console.error('Error fetching steps for order index:', error);
      } finally {
        setLoadingOrderIndex(false);
      }
    };
    
    fetchNextOrderIndex();
  }, [showModal, phaseId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stepName.trim()) {
      toast.error('Step name is required');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const response = await api.post(`/steps`, {
        phase_id: phaseId,
        name: stepName,
        description: stepDescription,
        status: 'Not Started',
        due_date: stepDueDate || null,
        video_url: videoUrl.trim() || null
      });
      
      toast.success('Step added successfully');
      setShowModal(false);
      setStepName('');
      setStepDescription('');
      setStepDueDate('');
      setVideoUrl('');
      
      // Call the callback to refresh the phase data
      if (onStepAdded) {
        onStepAdded(response.data.step);
      }
    } catch (error) {
      console.error('Error adding step:', error);
      toast.error(error.response?.data?.error || 'Failed to add step');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setShowModal(true);
        }}
        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <PlusIcon className="-ml-1 mr-1 h-4 w-4" aria-hidden="true" />
        {label}
      </button>
      
      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Add New Step
                      </h3>
                      
                      {nextOrderIndex !== null && (
                        <div className="mt-2 flex items-center text-sm text-blue-600 bg-blue-50 p-2 rounded">
                          <InformationCircleIcon className="h-5 w-5 mr-1" />
                          <span>This step will be added with order index: {nextOrderIndex}</span>
                        </div>
                      )}
                      
                      <div className="mt-2">
                        <div className="mb-4">
                          <label htmlFor="stepName" className="block text-sm font-medium text-gray-700">
                            Step Name *
                          </label>
                          <input
                            type="text"
                            name="stepName"
                            id="stepName"
                            value={stepName}
                            onChange={(e) => setStepName(e.target.value)}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder="Enter step name"
                            required
                          />
                        </div>
                        <div className="mb-4">
                          <label htmlFor="stepDescription" className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <textarea
                            id="stepDescription"
                            name="stepDescription"
                            rows={3}
                            value={stepDescription}
                            onChange={(e) => setStepDescription(e.target.value)}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder="Enter step description"
                          />
                        </div>
                        <div className="mb-4">
                          <label htmlFor="videoUrl" className="block text-sm font-medium text-gray-700">
                            Video URL
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <VideoCameraIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </div>
                            <input
                              type="url"
                              name="videoUrl"
                              id="videoUrl"
                              value={videoUrl}
                              onChange={(e) => setVideoUrl(e.target.value)}
                              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                              placeholder="https://example.com/video"
                            />
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            Enter a URL to a video that explains this step
                          </p>
                        </div>
                        <div>
                          <label htmlFor="stepDueDate" className="block text-sm font-medium text-gray-700">
                            Due Date
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <CalendarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </div>
                            <input
                              type="date"
                              name="stepDueDate"
                              id="stepDueDate"
                              value={stepDueDate}
                              onChange={(e) => setStepDueDate(e.target.value)}
                              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isSubmitting || !stepName.trim() || loadingOrderIndex}
                    className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm ${
                      (isSubmitting || !stepName.trim() || loadingOrderIndex) ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? 'Adding...' : 'Add Step'}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowModal(false);
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddStepButton; 